import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  split  
} from "@apollo/client"
import { createUploadLink } from 'apollo-upload-client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from "apollo-link-ws";
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { store } from '@store/storeConfig/store';
import { handleLogout } from '@store/actions/auth';
import {
  MFA_PENDING_STATUS_KEY,
  MFA_PENDING_PATH,
  allowMfaRedirect,
  removeCurrentAccountFromList,
  resetMfaRedirectGuard,
  setLoginNotice
} from '@src/app/login/mfaUtils';

const {
  REACT_APP_GQL_URI,
  REACT_APP_GQL_BACKEND_ENDPOINT,
  REACT_APP_ANALYTICS_GQL_URI
} = process.env

const LOGIN_OPERATIONS = ['SignIn', 'DisplayCode', 'VerifyMfa', 'RecoverMfa', 'RequestMfaEmailOtp', 'VerifyMfaEmailOtp']

const goTo = (path) => {
  if (window.location.pathname !== path) {
    window.location.assign(path)
  }
}

const collectErrors = (graphQLErrors, networkError) => {
  if (graphQLErrors && graphQLErrors.length > 0) return graphQLErrors
  const result = networkError && networkError.result
  return (result && Array.isArray(result.errors)) ? result.errors : []
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (operation && LOGIN_OPERATIONS.includes(operation.operationName)) {
    return
  }
  for (const { extensions } of collectErrors(graphQLErrors, networkError)) {
    const code = extensions && extensions.code
    if (code === 'MFA_PENDING') {
      localStorage.setItem(MFA_PENDING_STATUS_KEY, extensions.mfaStatus || 'CODE_REQUIRED')
      if (window.location.pathname !== MFA_PENDING_PATH && allowMfaRedirect()) {
        window.location.assign(MFA_PENDING_PATH)
      }
      return
    }
    if (code === 'UNAUTHENTICATED' && extensions.reason) {
      removeCurrentAccountFromList()
      store.dispatch(handleLogout())
      localStorage.removeItem('usertoken')
      localStorage.removeItem(MFA_PENDING_STATUS_KEY)
      resetMfaRedirectGuard()
      setLoginNotice(extensions.reason)
      goTo('/login')
      return
    }
  }
})

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('access_token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      bqtok: token ? token : '',
      bqmobtok: localStorage.getItem("code_token") || null,
      bqmobapp: localStorage.getItem("app") || null,
      usertoken: localStorage.getItem("usertoken") || null
    }
  }
})


const httpLinuploadLinkk = createUploadLink({
  uri: REACT_APP_GQL_URI,
});

const analyticsHttpLink = createUploadLink({
  uri: REACT_APP_ANALYTICS_GQL_URI,
});


const wsLink = new WebSocketLink({
  uri: REACT_APP_GQL_BACKEND_ENDPOINT,
  options: {
    reconnect: true
  }
});

const wsLinkV2 = new GraphQLWsLink(
  createClient({
    url: REACT_APP_GQL_URI.replace(/^https?:/, (protocol) => (protocol === 'https:' ? 'wss:' : 'ws:')),
    connectionParams: () => ({
      authorization: localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : '',
      bqtok: localStorage.getItem('access_token') || '',
      bqmobtok: localStorage.getItem('code_token') || '',
      bqmobapp: localStorage.getItem('app') || '',
      usertoken: localStorage.getItem('usertoken') || '',
    })
  })
)

const REQUISITION_SUBSCRIPTIONS = ['RequisitionActivityCreated', 'RequisitionActivityStatusChanged']

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  split(
    ({ operationName }) => REQUISITION_SUBSCRIPTIONS.includes(operationName),
    wsLinkV2,
    wsLink
  ),
  split(
    ({ operationName }) => operationName && operationName.startsWith('AnalyticsGql'),
    authLink.concat(analyticsHttpLink),
    authLink.concat(httpLinuploadLinkk)
  )
);

const client = new ApolloClient({  
  link: errorLink.concat(splitLink),
  cache: new InMemoryCache(),      
})


export default client
